import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import LeadFunnel from '../components/LeadFunnel';
import TeamAnalysisCard from '../components/TeamAnalysisCard';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, ArrowUpRight, ArrowDownRight, Minus, AlertTriangle } from 'lucide-react';

// ============================================================
// LEAD PERFORMANCE
//
// Lead-journey lens on the team data. Complements the per-BDE Team
// Dashboard. Reads /stats/lead-performance (admin + team_lead + viewer).
//
// Page-level date range drives all sections EXCEPT:
//   • Momentum strip — always this week (Mon–Sun) vs last week per spec.
//   • Stalled — range-independent ("no activity in 7+ days, now").
//
// Classification + Source % advanced-to-call uses lead-level
// discovery_done_at (Phase 2/3 adoption gap means values look low
// until the dropdown habit lands; caveat label on the cards).
// ============================================================

const fmtDate = (d) => d.toISOString().split('T')[0];
const getMonday = (d) => {
  const day = d.getDay();
  const offset = day === 0 ? 6 : day - 1;
  const m = new Date(d);
  m.setDate(d.getDate() - offset);
  return m;
};
const addDays = (d, n) => {
  const o = new Date(d);
  o.setDate(d.getDate() + n);
  return o;
};

const PRESET_OPTIONS = [
  { key: 'this_week',    label: 'This week' },
  { key: 'last_week',    label: 'Last week' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'custom',       label: 'Custom range' },
];

// Source enum → human label. Backend returns the raw enum value; the
// labels here keep the UI consistent with how the rest of the app
// names these (Add Lead form, etc.). Any future source values fall
// through to the raw enum as a safety net.
const SOURCE_LABELS = {
  linkedin:        'LinkedIn',
  upwork_inbound:  'Upwork Inbound',
  upwork_research: 'Upwork Research',
};

const getRangeFor = (preset, custom) => {
  const today = new Date();
  switch (preset) {
    case 'this_week': {
      const m = getMonday(today);
      return { start: fmtDate(m), end: fmtDate(addDays(m, 6)) };
    }
    case 'last_week': {
      const m = addDays(getMonday(today), -7);
      return { start: fmtDate(m), end: fmtDate(addDays(m, 6)) };
    }
    case 'last_30_days':
      return { start: fmtDate(addDays(today, -29)), end: fmtDate(today) };
    case 'custom':
      return { start: custom.start || '', end: custom.end || '' };
    default:
      return { start: fmtDate(addDays(today, -29)), end: fmtDate(today) };
  }
};

// ============================================================
// MOMENTUM CARD
// ============================================================
const Delta = ({ current, previous }) => {
  if (previous === 0 && current === 0) {
    return <span style={{ color: '#9CA3AF', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Minus size={12} /> flat
    </span>;
  }
  if (previous === 0) {
    return <span style={{ color: '#10B981', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <ArrowUpRight size={12} /> new
    </span>;
  }
  const diff = current - previous;
  const pct = Math.round((diff / previous) * 100);
  if (diff === 0) {
    return <span style={{ color: '#9CA3AF', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Minus size={12} /> 0%
    </span>;
  }
  const isUp = diff > 0;
  return (
    <span style={{
      color: isUp ? '#10B981' : '#EF4444',
      fontSize: 12,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
    }}>
      {isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {isUp ? '+' : ''}{pct}% vs last week
    </span>
  );
};

const MomentumCard = ({ label, current, previous }) => (
  <div style={{
    background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16,
  }}>
    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>{current}</div>
      <div style={{ fontSize: 12, color: '#9CA3AF' }}>last week: {previous}</div>
    </div>
    <div style={{ marginTop: 6 }}>
      <Delta current={current} previous={previous} />
    </div>
  </div>
);

// ============================================================
// CLASSIFICATION CARD
// ============================================================
const CLASSIFICATION_PALETTE = {
  hot:  { accent: '#DC2626', bg: '#FEF2F2', label: 'Hot' },
  warm: { accent: '#F59E0B', bg: '#FFFBEB', label: 'Warm' },
  cold: { accent: '#2563EB', bg: '#EFF6FF', label: 'Cold' },
};

const ClassificationCard = ({ entry }) => {
  const p = CLASSIFICATION_PALETTE[entry.key] || CLASSIFICATION_PALETTE.cold;
  return (
    <div style={{
      background: p.bg, border: `1px solid ${p.accent}33`,
      borderLeft: `4px solid ${p.accent}`,
      borderRadius: 8, padding: 16,
    }}>
      <div style={{ fontSize: 12, color: p.accent, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>
        {p.label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>
        {entry.count}
      </div>
      <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
        leads
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 12, color: '#6B7280' }}>Advanced to call</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: '#111827' }}>
          {entry.advanced_to_call} ({entry.advanced_to_call_pct}%)
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STALLED ROW
// ============================================================
const StalledRow = ({ row, onClick, maxCount }) => {
  const widthPct = maxCount > 0 ? Math.max(4, Math.round((row.count / maxCount) * 100)) : 0;
  return (
    <tr style={{ cursor: 'pointer' }} onClick={onClick}>
      <td style={{ padding: '10px 12px', fontSize: 14, color: '#111827' }}>
        {row.stage_label}
      </td>
      <td style={{ padding: '10px 12px' }}>
        <div style={{
          height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden',
          border: '1px solid #E5E7EB',
        }}>
          <div style={{
            width: `${widthPct}%`, height: '100%', background: '#EF4444',
          }} />
        </div>
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#991B1B' }}>
        {row.count}
      </td>
    </tr>
  );
};

// ============================================================
// PAGE
// ============================================================
const LeadPerformance = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [preset, setPreset] = useState('last_30_days');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const range = getRangeFor(preset, custom);
  const canFetch = !!(range.start && range.end && range.start <= range.end);

  useEffect(() => {
    if (!canFetch) return;
    setLoading(true);
    setError('');
    api.get('/stats/lead-performance', { params: { start: range.start, end: range.end } })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load lead performance.'))
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  const handleStalledClick = (stage) => {
    navigate(`/leads?lead_status=${stage}&stalled=1`);
  };

  return (
    <div>
      <div className="page-header" style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TrendingUp size={22} color="#3B82F6" /> Lead Performance
          </h2>
          <p>Lead-journey lens on the funnel — complements the per-BDE Team Dashboard</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
              Range
            </label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              style={{
                padding: '6px 10px', border: '1px solid #D1D5DB',
                borderRadius: 6, fontSize: 14, background: '#FFFFFF', cursor: 'pointer',
              }}
            >
              {PRESET_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Start</label>
                <input
                  type="date"
                  value={custom.start}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={{ padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>End</label>
                <input
                  type="date"
                  value={custom.end}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={{ padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          padding: 12, background: '#FEE2E2', color: '#991B1B',
          borderRadius: 6, fontSize: 14, marginBottom: 16,
        }}>{error}</div>
      )}

      {loading && !data ? (
        <div className="empty-state"><p>Loading lead performance…</p></div>
      ) : !data ? null : (
        <>
          {/* SECTION 1 — Momentum strip (fixed weekly compare) */}
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginTop: 24, marginBottom: 8,
          }}>
            Momentum — this week ({data.momentum.this_week.range.from} → {data.momentum.this_week.range.to})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <MomentumCard
              label="Leads added"
              current={data.momentum.this_week.leads_added}
              previous={data.momentum.last_week.leads_added}
            />
            <MomentumCard
              label="Calls held"
              current={data.momentum.this_week.calls_held}
              previous={data.momentum.last_week.calls_held}
            />
            <MomentumCard
              label="Proposals sent"
              current={data.momentum.this_week.proposals_sent}
              previous={data.momentum.last_week.proposals_sent}
            />
            <MomentumCard
              label="Closed-won"
              current={data.momentum.this_week.closed_won}
              previous={data.momentum.last_week.closed_won}
            />
          </div>

          {/* SECTION 2 — Funnel (headline) */}
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginTop: 28, marginBottom: 8,
          }}>
            Lead Funnel — {data.period.label}
          </div>
          <LeadFunnel stages={data.funnel.stages} />

          {/* SECTION 3 — Classification */}
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginTop: 28, marginBottom: 8,
          }}>
            Classification — {data.period.label}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {data.classification.map((c) => (
              <ClassificationCard key={c.key} entry={c} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
            “Advanced to call” uses lead-level <code>discovery_done_at</code>; counts may
            lag until the team's dropdown habit lands (Phase 2/3 shipped 2026-05-13).
          </div>

          {/* SECTION 4 — Stalled by stage */}
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginTop: 28, marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertTriangle size={14} color="#EF4444" />
            Stalled by stage — no activity in 7+ days
          </div>
          {data.stalled.length === 0 ? (
            <div style={{
              padding: 16, background: '#ECFDF5', border: '1px solid #A7F3D0',
              borderRadius: 6, fontSize: 14, color: '#065F46',
            }}>
              No stalled leads. The pipeline is moving.
            </div>
          ) : (
            <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Stage</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#6B7280', fontWeight: 600 }}> </th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const max = Math.max(...data.stalled.map((r) => r.count), 1);
                    return data.stalled.map((row) => (
                      <StalledRow
                        key={row.stage}
                        row={row}
                        maxCount={max}
                        onClick={() => handleStalledClick(row.stage)}
                      />
                    ));
                  })()}
                </tbody>
              </table>
              <div style={{ padding: '6px 12px', fontSize: 11, color: '#9CA3AF', background: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
                Click a row to open the Leads page filtered to that stage's stalled leads.
              </div>
            </div>
          )}

          {/* SECTION 5 — Source breakdown */}
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginTop: 28, marginBottom: 8,
          }}>
            Source — {data.period.label}
          </div>
          {data.source.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6 }}>
              No leads in this range.
            </div>
          ) : (
            <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left',  fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Source</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Total</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#DC2626', fontWeight: 600 }}>Hot</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#F59E0B', fontWeight: 600 }}>Warm</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#2563EB', fontWeight: 600 }}>Cold</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>% advanced to call</th>
                  </tr>
                </thead>
                <tbody>
                  {data.source.map((row) => (
                    <tr key={row.source} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 12px', fontSize: 14, color: '#111827' }}>
                        {SOURCE_LABELS[row.source] || row.source}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#111827' }}>
                        {row.total}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#DC2626' }}>{row.hot}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#F59E0B' }}>{row.warm}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#2563EB' }}>{row.cold}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#111827' }}>{row.advanced_to_call_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* AI Team Analysis (admin-only) */}
          {isAdmin && (
            <TeamAnalysisCard rangeStart={range.start} rangeEnd={range.end} />
          )}
        </>
      )}
    </div>
  );
};

export default LeadPerformance;
